import { isSessionExpired } from '@/api/resources/auth.api';
import { clearLocalSession } from '@/auth/clearLocalSession';
import { useAuthStore } from '@/stores/useAuthStore';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { isPdfPrintPath } from '@/utils/pdfPrint.utils';
import { getCurrentTenant, getHasProviderAccess, getHasTenantAdminAccess } from '@/utils/role.utils';
import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';

/**
 * Sends a signed-in user from an auth page into the app. The persisted csrf token outlives expired/revoked sessions,
 * and a stale one would bounce register, login and the mail links via '/' and its 401 logout to '/auth/login' — so
 * forget it instead and open the requested page.
 */
const redirectToAppUnlessSessionExpired = async (
  to: RouteLocationNormalized,
): Promise<RouteLocationRaw | undefined> => {
  if (await isSessionExpired()) {
    await clearLocalSession();
    return undefined;
  }

  return { path: '/', query: to.query };
};

export const setupGuards = (router: Router): void => {
  router.beforeEach(async (to) => {
    const authStore = useAuthStore();
    const whoAmIStore = useWhoAmIStore();
    const isLoggedIn = authStore.csrfToken != null;
    const isSetupCompleted = whoAmIStore.whoAmI?.isSetupCompleted;
    const hasCompletedUserOnboarding = isSetupCompleted && whoAmIStore.whoAmI?.isApproved;
    const hasProviderAccess = getHasProviderAccess(whoAmIStore.whoAmI);
    const hasTenantAdminAccess = getHasTenantAdminAccess(whoAmIStore.whoAmI);
    const currentTenant = getCurrentTenant(whoAmIStore.whoAmI);

    const allowAuthPagesWhenLoggedIn = ['/auth/email-change'];
    const allowRoutesBeforeOnboarding = ['/auth/email-change', '/user/invitations'];
    const isAllowedAuthPageWhenLoggedIn = allowAuthPagesWhenLoggedIn.includes(to.path);
    const isAllowedRouteBeforeOnboarding = allowRoutesBeforeOnboarding.includes(to.path);
    const isWizardRoute = to.path.includes('/wizard');
    const isSchoolRoute = to.path.startsWith('/school');

    const isPublicRoute =
      to.path.startsWith('/auth') ||
      to.path.startsWith('/legal') ||
      to.path.startsWith('/user') ||
      isPdfPrintPath(to.path) ||
      to.path.startsWith('/referral');

    // Redirect to login page if the user is not logged in and is trying to access a page that requires authentication
    if (!isPublicRoute && !isLoggedIn) {
      return { path: '/auth/login', query: to.query };
    }

    if (isLoggedIn) {
      // Redirect to home page if the user is logged in and is trying to access the login page
      if (to.path.startsWith('/auth')) {
        if (!isAllowedAuthPageWhenLoggedIn) {
          return redirectToAppUnlessSessionExpired(to);
        }

        return; // allow access to auth pages that are allowed when logged in
      }

      // Redirect to home page if the user is not a provider and is trying to access the provider page
      if (to.path.startsWith('/provider') && !hasProviderAccess) {
        return { path: '/', query: to.query };
      }

      // Redirect to home page if the user is not a tenant admin and is trying to access the tenant admin page
      if (to.path.startsWith('/school') && !hasTenantAdminAccess) {
        return { path: '/', query: to.query };
      }

      //if the user hasnt been onboarded restrict access to the user onboarding
      if (!isAllowedRouteBeforeOnboarding && !to.path.startsWith('/onboarding/user') && !hasCompletedUserOnboarding) {
        return '/onboarding/user';
      }

      //if the user is onboarded don't allow him to visit the page again!
      if (to.path.startsWith('/onboarding/user') && hasCompletedUserOnboarding) {
        return '/onboarding/school';
      }

      //if the user hasnt been onboarded restrict access to the user onboarding
      if (
        !to.path.startsWith('/onboarding/school') &&
        !to.path.startsWith('/profile') &&
        !isAllowedRouteBeforeOnboarding &&
        hasCompletedUserOnboarding &&
        !currentTenant
      ) {
        return '/onboarding/school';
      }

      // Keep old wizard links from emails/bookmarks working after the administration setup refactor.
      if (hasTenantAdminAccess && !hasProviderAccess && isSetupCompleted && isSchoolRoute) {
        if (isWizardRoute && currentTenant?.id) {
          return `/school/${currentTenant.id}/settings`;
        }
      }
    }
  });
};

export default setupGuards;
